import { puedeVerCobros } from '../lib/cobros.js';
import { useConfirmar } from '../lib/dialogos.js';
import { useAuth } from '../lib/auth.js';
import {
  CICLOS_DE_COBRO,
  daAcceso,
  type CicloDeCobro,
  type Suscripcion,
} from '@qr/shared/billing';
import {
  MESES_GRATIS_AL_ANO,
  MESES_QUE_SE_PAGAN,
  PLANS,
  isUnlimited,
  listedPlans,
  formatUsd,
  yearlyMonthlyEquivalentUsdCents,
  yearlyTotalUsdCents,
  type PlanId,
} from '@qr/shared/plans';
/*
 * Las lineas que describen cada plan salen de @qr/shared, las mismas que
 * lee la portada.
 *
 * Esta pantalla llego a ensenar tres: codigos, escaneos y personas. La
 * portada ensenaba ocho. Quien llegaba aqui desde "Elegir Pro" veia menos
 * producto del que acababa de leer, justo en el momento de sacar la
 * tarjeta, y lo que falta en una pagina de pago no se lee como un olvido
 * sino como letra pequena.
 */
import { ahorroAnual, rasgosDe } from '@qr/shared/plan-copy';
import { Check, Minus } from 'lucide-react';
import { Button, Callout, Spinner } from '@qr/ui';
import { doc, onSnapshot } from 'firebase/firestore';
import { useEffect, useState } from 'react';
import { useSearchParams, Navigate } from 'react-router';
import { api, messageFromError } from '../lib/api.js';
import { db } from '../lib/firebase.js';
import { useCan, useOrg } from '../lib/org.js';

/**
 * Facturacion: contratar un plan, cambiar la tarjeta y darse de baja.
 *
 * El pago no ocurre aqui. Esta pantalla pide al servidor una direccion y lleva
 * alli a la persona, al dominio del proveedor, que es donde se teclea la
 * tarjeta. Por el panel no pasa ningun numero de tarjeta, y eso es
 * deliberado: es lo que mantiene el cumplimiento de PCI en su nivel mas
 * sencillo y lo que hace que una brecha en este codigo no sea una brecha de
 * medios de pago.
 *
 * Los precios salen de `plans.ts` y el importe que se cobra de verdad lo fija
 * la variante en el panel del proveedor. Aqui no hay ni un numero escrito a
 * mano: si manana sube el plan Pro, sube en un solo sitio.
 */

/*
 * Los dos decimales siempre: el equivalente mensual del plan anual sale 7,50 y
 * un ".5" a medias parece un error del programa, no un precio.
 */
const DOLARES = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/*
 * De centavos a texto. Recibe CENTAVOS, y por eso lo dice el nombre.
 *
 * Los precios se guardan en la unidad minima —900 son nueve dolares— porque es
 * lo que cobran las pasarelas y porque guardar 9.99 en coma flotante es como
 * se pierde un centavo en una factura. El riesgo de eso es olvidar la
 * division: esta pantalla llego a ensenar "$900.00 / mes" por pasarle los
 * centavos a un formateador que esperaba dolares.
 *
 * Con la division dentro de la funcion, el error exige escribir
 * `enDolares(900 * 100)`, que se ve al leerlo.
 */
function enDolares(centavos: number): string {
  return DOLARES.format(centavos / 100);
}

const fecha = new Intl.DateTimeFormat('es-CO', { dateStyle: 'long' });

function comoFecha(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : fecha.format(d);
}

/** Como se le cuenta al cliente el estado que manda el proveedor. */
const ESTADO_EN_CLARO: Record<string, string> = {
  on_trial: 'En periodo de prueba',
  active: 'Activa',
  paused: 'En pausa',
  past_due: 'Pendiente de cobro',
  unpaid: 'Sin pagar',
  cancelled: 'Dada de baja',
  expired: 'Terminada',
};

export function Facturacion() {
  const org = useOrg();
  const esPropietario = useCan('owner');
  const { user: usuario, isSuperadmin } = useAuth();
  const [parametros] = useSearchParams();
  const confirmar = useConfirmar();

  const [suscripcion, setSuscripcion] = useState<Suscripcion | null | undefined>(undefined);
  /*
   * El ciclo que venia elegido de la portada.
   *
   * Quien pulsa "Elegir Pro" estando en la pestaña de anual espera encontrarse
   * anual al llegar, no tener que volver a elegirlo. Se lee una sola vez, al
   * montar: a partir de ahi manda el interruptor, y que la URL siguiera
   * pisandolo haria imposible cambiarlo.
   *
   * La portada habla de "mes" y "ano" y aqui se llaman "mensual" y "anual",
   * asi que se traduce. Cualquier otra cosa cae en mensual, que es lo que
   * habia antes de todo esto.
   */
  const [ciclo, setCiclo] = useState<CicloDeCobro>(() =>
    parametros.get('ciclo') === 'ano' || parametros.get('ciclo') === 'anual'
      ? 'anual'
      : 'mensual',
  );

  /*
   * El plan que venia elegido, solo para destacarlo.
   *
   * No se contrata solo al llegar a proposito: una pagina que cobra al
   * cargarse vuelve a cobrar si alguien refresca o vuelve atras. El ultimo
   * clic lo da la persona.
   */
  const planPedido = parametros.get('plan');
  const [error, setError] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState<string | null>(null);

  /*
   * El documento de la suscripcion solo lo puede leer el propietario (asi lo
   * fijan las reglas), de modo que ni se intenta para los demas: una lectura
   * denegada se veria en la consola como un error y no es ninguno.
   */
  useEffect(() => {
    if (!org.orgId || !esPropietario) return;
    return onSnapshot(
      doc(db, `orgs/${org.orgId}/billing/suscripcion`),
      (snap) => setSuscripcion(snap.exists() ? (snap.data() as Suscripcion) : null),
      (caught) => {
        // Sin este registro el sintoma seria una pantalla que dice "no tienes
        // suscripcion" a alguien que si la tiene y esta pagando.
        console.error('No se pudo leer la suscripcion', caught);
        setSuscripcion(null);
      },
    );
  }, [org.orgId, esPropietario]);

  async function contratar(plan: PlanId) {
    if (!org.orgId) return;
    setError(null);
    setOcupado(`contratar:${plan}`);
    try {
      const { url } = await api.iniciarPago({ orgId: org.orgId, plan, ciclo });
      // Se sale del panel hacia el proveedor. No se abre en otra pestana: en
      // movil una pestana nueva para pagar se pierde con facilidad, y al
      // volver el proveedor nos trae de vuelta a esta misma pagina.
      window.location.assign(url);
    } catch (caught) {
      setError(messageFromError(caught));
      setOcupado(null);
    }
  }

  async function abrirPortal() {
    if (!org.orgId) return;
    setError(null);
    setOcupado('portal');
    try {
      const { url } = await api.portalDeFacturacion({ orgId: org.orgId });
      window.location.assign(url);
    } catch (caught) {
      setError(messageFromError(caught));
      setOcupado(null);
    }
  }

  async function darDeBaja() {
    if (!org.orgId) return;
    // Una baja apaga los codigos impresos del negocio cuando termine el
    // periodo: se pregunta antes, y se dice que pasa y cuando.
    const seguro = await confirmar({
      titulo: 'Dar de baja tu plan',
      mensaje:
        'Seguirá activo hasta el final del periodo que ya has pagado. Después tu cuenta volverá al plan Gratis.',
      aceptar: 'Dar de baja',
      cancelar: 'Seguir con el plan',
    });
    if (!seguro) return;

    setError(null);
    setOcupado('baja');
    try {
      await api.cancelarPlan({ orgId: org.orgId });
    } catch (caught) {
      setError(messageFromError(caught));
    } finally {
      setOcupado(null);
    }
  }

  if (org.loading) {
    return (
      <div className="center">
        <Spinner label="Cargando tu cuenta" />
      </div>
    );
  }

  /*
   * Con el cobro cerrado esta pantalla no existe para nadie salvo un
   * superadmin, aunque se llegue escribiendo la URL.
   *
   * Esconder la entrada del menu no basta: ensenarle las tarjetas de precios a
   * alguien y que al pulsar le salte un error es peor que no ensenarselas. Se
   * devuelve a la pantalla principal sin explicacion porque, para quien llega
   * aqui a mano, la seccion sencillamente todavia no esta.
   *
   * Lo que de verdad impide pagar es la puerta del servidor, en
   * functions/src/api/facturacion.ts. Esto es presentacion.
   */
  if (!puedeVerCobros(usuario?.email, isSuperadmin)) return <Navigate to="/" replace />;

  return (
    <div className="flex flex-col">
      <header className="flex flex-wrap items-center gap-3 border-b border-line px-4 py-3 lg:px-6">
        <h1 className="m-0 text-lg font-semibold tracking-tight">Facturación</h1>
        <span className="rounded-full border border-line px-2 py-0.5 text-[11.5px] text-ink-3">
          Plan {org.planName}
        </span>
      </header>

      <div className="flex max-w-[72rem] flex-col gap-5 px-4 py-5 lg:px-6">
        {/*
          La vuelta del pago no concede nada.

          El plan lo aplica el aviso firmado del proveedor, que puede tardar
          unos segundos en llegar. Si esta pantalla diera el plan por hecho,
          bastaria con escribir esta direccion en la barra para "contratar".
        */}
        {parametros.get('pago') === 'recibido' ? (
          <Callout tone="success" title="Hemos recibido tu pago">
            Gracias. Tu plan se activa en cuanto el proveedor nos confirme el
            cobro, normalmente en unos segundos. Esta página se actualiza sola.
          </Callout>
        ) : null}

        {error ? <Callout tone="danger">{error}</Callout> : null}

        {org.isComped ? (
          <Callout tone="info" title="Tu cuenta está en uso libre">
            No tienes ningún cobro asociado y no hay nada que gestionar aquí.
            Si necesitas una factura o quieres pasar a un plan de pago,
            escríbenos.
          </Callout>
        ) : !esPropietario ? (
          <Callout tone="info" title="Solo lo ve quien es propietario">
            La facturación de esta cuenta la lleva su propietario. Pídele a
            quien te invitó que haga el cambio de plan.
          </Callout>
        ) : (
          <>
            {suscripcion === undefined && org.orgId ? (
              <div className="center">
                <Spinner label="Cargando tu suscripción" />
              </div>
            ) : suscripcion && daAcceso(suscripcion.estado) ? (
              <EstadoDeLaSuscripcion
                suscripcion={suscripcion}
                ocupado={ocupado}
                onPortal={() => void abrirPortal()}
                onBaja={() => void darDeBaja()}
              />
            ) : null}

            {/*
              Lo que llevas gastado este mes, antes de los precios.

              Sin esta linea, la pantalla pide una decision sobre consumo sin
              ensenar el consumo: "¿me hace falta Pro?" no se responde mirando
              una tabla de limites, se responde sabiendo por donde vas. Y en el
              gratuito importa mas, porque al agotar los escaneos el codigo
              impreso DEJA de redirigir.
            */}
            <ConsumoDeAhora
              escaneos={org.scansThisMonth}
              tope={org.limits.scansPerMonth}
              planActual={org.planName}
            />

            <SelectorDeCiclo ciclo={ciclo} onCambio={setCiclo} />

            {/*
              El gratuito tambien es una tarjeta.

              Sin el, la pantalla ensena dos precios y ninguna referencia: no
              se ve de donde sale quien esta mirando ni que gana al cambiar.
              Comparar es justo lo que se viene a hacer aqui.
            */}
            <ul className="m-0 grid list-none gap-3 p-0 sm:grid-cols-2 lg:grid-cols-3">
              {listedPlans().map((plan) => (
                <TarjetaDePlan
                  key={plan.id}
                  plan={plan.id}
                  ciclo={ciclo}
                  esElActual={org.plan === plan.id}
                  destacado={planPedido === plan.id}
                  bloqueado={suscripcion !== null && suscripcion !== undefined && daAcceso(suscripcion.estado)}
                  ocupado={ocupado === `contratar:${plan.id}`}
                  onContratar={() => void contratar(plan.id)}
                />
              ))}
            </ul>

            <p className="m-0 text-[12.5px] text-ink-2">
              Los planes de pago <strong className="font-semibold text-ink">no tienen tope de
              escaneos</strong>: un código impreso no deja de funcionar a mitad de mes. El plan
              anual se cobra de una vez y equivale a pagar {MESES_QUE_SE_PAGAN} meses de 12.
            </p>

            <p className="m-0 text-[12.5px] text-ink-3">
              Los cobros los gestiona Lemon Squeezy, que figura como vendedor
              en tu factura y se encarga de los impuestos que correspondan a tu
              país. Puedes descargar tus facturas y cambiar la tarjeta desde
              «Gestionar mi suscripción».
            </p>
          </>
        )}
      </div>
    </div>
  );
}

function SelectorDeCiclo({
  ciclo,
  onCambio,
}: {
  readonly ciclo: CicloDeCobro;
  readonly onCambio: (ciclo: CicloDeCobro) => void;
}) {
  return (
    <div
      className="flex items-center gap-1 self-start rounded-full border border-line p-1"
      role="group"
      aria-label="Periodo de pago"
    >
      {CICLOS_DE_COBRO.map((valor) => (
        <button
          key={valor}
          type="button"
          aria-pressed={ciclo === valor}
          onClick={() => onCambio(valor)}
          className={`rounded-full px-3 py-1 text-[12.5px] ${
            ciclo === valor ? 'bg-raise font-semibold text-ink' : 'text-ink-2'
          }`}
        >
          {valor === 'mensual' ? 'Mes a mes' : 'Un año'}
          {/* Los meses gratis salen de la constante, no de un "-17%" escrito
              a mano: es lo que se le dice al cliente y debe cuadrar con lo que
              se le cobra. */}
          {valor === 'anual' ? (
            <span className="ms-1.5 text-[11.5px] font-semibold text-accent">
              {MESES_GRATIS_AL_ANO} meses gratis
            </span>
          ) : null}
        </button>
      ))}
    </div>
  );
}

function TarjetaDePlan({
  plan,
  ciclo,
  esElActual,
  destacado,
  bloqueado,
  ocupado,
  onContratar,
}: {
  readonly plan: PlanId;
  readonly ciclo: CicloDeCobro;
  readonly esElActual: boolean;
  /** Venia elegido desde la portada. */
  readonly destacado: boolean;
  readonly bloqueado: boolean;
  readonly ocupado: boolean;
  readonly onContratar: () => void;
}) {
  const datos = PLANS[plan];
  const esGratuito = datos.monthlyPriceUsdCents === 0;
  const alMes = ciclo === 'mensual' ? datos.monthlyPriceUsdCents : yearlyMonthlyEquivalentUsdCents(datos);
  const totalDelAno = yearlyTotalUsdCents(datos);

  return (
    /*
     * El plan que venia elegido se enmarca.
     *
     * Quien llega aqui desde "Elegir Pro" ha dicho ya lo que quiere; la
     * pantalla tiene que reconocerlo en lugar de presentarle otra vez la misma
     * eleccion. Es un marco, no un boton distinto: el ultimo clic sigue siendo
     * suyo y deliberado.
     */
    <li
      className={`flex flex-col gap-2 rounded-lg border bg-surface px-4 py-4 ${
        destacado ? 'border-brand ring-1 ring-brand' : 'border-line'
      }`}
    >
      <div className="flex items-baseline gap-2">
        <strong className="text-[15px]">{datos.name}</strong>
        {esElActual ? (
          <span className="rounded-full bg-brand-soft px-2 py-0.5 text-[11px] font-semibold text-brand-deep">
            Tu plan
          </span>
        ) : null}
      </div>

      <p className="m-0 text-[20px] font-semibold tabular-nums">
        {/* El gratuito, sin decimales: dos ceros detras de la coma no dicen
            nada y ese numero no tiene de donde a donde cambiar. */}
        {alMes === null ? '—' : esGratuito ? formatUsd(0) : enDolares(alMes)}
        <span className="text-[12.5px] font-normal text-ink-3"> / mes</span>
      </p>

      {/*
        Debajo del precio, lo que de verdad se cobra.

        En anual, el numero grande es un equivalente que nadie paga doce veces:
        callarse el total es la forma educada de dar una sorpresa en el cargo.
        En mensual se dice lo que cuesta el ano, porque si no la unica manera
        de enterarse del descuento es pulsar la otra pestana a ciegas.
      */}
      <p className="m-0 min-h-[1.1rem] text-[12px] text-ink-3 tabular-nums">
        {esGratuito
          ? 'Sin tarjeta y sin caducidad'
          : ciclo === 'anual'
            ? totalDelAno === null
              ? ''
              : `${formatUsd(totalDelAno)} al año, en un solo cobro`
            : (ahorroAnual(datos) ?? '')}
      </p>

      {/*
        Todo lo que trae el plan, y tambien lo que no.

        Las lineas tachadas no son relleno: son la diferencia entre este plan y
        el de al lado, que es exactamente lo que se viene a mirar. Un listado
        que solo ensena lo incluido obliga a deducir las ausencias comparando
        longitudes de lista.
      */}
      <ul className="m-0 flex list-none flex-col gap-1.5 p-0 text-[12.5px]">
        {rasgosDe(datos.limits).map((rasgo) => (
          <li
            key={rasgo.texto}
            className={`flex items-start gap-1.5 ${rasgo.incluido ? 'text-ink-2' : 'text-ink-3'}`}
          >
            <span className="mt-0.5 shrink-0" aria-hidden>
              {rasgo.incluido ? (
                <Check size={13} strokeWidth={2.6} className="text-brand" />
              ) : (
                <Minus size={13} strokeWidth={2.6} />
              )}
            </span>
            {/* Para quien no ve el icono, la ausencia tiene que decirse con
                palabras: sin esto, un lector de pantalla lee la lista entera
                como si todo estuviera incluido. */}
            {!rasgo.incluido ? <span className="sr-only">No incluido: </span> : null}
            <span>{rasgo.texto}</span>
          </li>
        ))}
      </ul>

      {/*
        El gratuito no se contrata: se es o no se es.

        Un boton "Contratar" en la columna de cero dolares no lleva a ninguna
        parte, y uno que diga "Bajar de plan" seria mentira: la baja se pide
        desde la suscripcion de arriba, que es quien sabe hasta cuando esta
        pagado el periodo en curso.
      */}
      {esGratuito ? (
        <p className="mt-1 m-0 text-[12px] text-ink-3">
          {esElActual
            ? 'Es donde estás ahora.'
            : 'Es a donde vuelve tu cuenta si das de baja el plan.'}
        </p>
      ) : (
        <Button
          className="mt-1 self-start"
          variant={esElActual ? 'secondary' : 'primary'}
          loading={ocupado}
          // Con una suscripcion viva, contratar otra cobraria dos veces: el
          // cambio de plan se hace desde el portal, que prorratea lo pagado.
          disabled={esElActual || bloqueado}
          onClick={onContratar}
        >
          {esElActual ? 'Plan contratado' : bloqueado ? 'Cambiar desde el portal' : 'Contratar'}
        </Button>
      )}
    </li>
  );
}

/**
 * Por donde va la cuenta este mes.
 *
 * Solo escaneos: es el unico consumo que el panel ya tiene en vivo y es
 * ademas el que muerde, porque al agotarlo el codigo deja de redirigir. Los
 * codigos creados se cuentan en otra pantalla y traerlos aqui obligaria a una
 * lectura mas en cada visita a facturacion.
 */
function ConsumoDeAhora({
  escaneos,
  tope,
  planActual,
}: {
  readonly escaneos: number;
  readonly tope: number;
  readonly planActual: string;
}) {
  const sinTope = isUnlimited(tope);
  // Se topa en 100: una barra que se sale de su caja se lee como un fallo de
  // dibujo, no como "te has pasado".
  const porcentaje = sinTope ? 0 : Math.min(100, Math.round((escaneos / tope) * 100));
  const apurado = !sinTope && porcentaje >= 80;

  return (
    <div className="flex flex-col gap-2 rounded-lg border border-line bg-surface px-4 py-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="text-[12.5px] text-ink-2">
          Escaneos de este mes en el plan {planActual}
        </span>
        <span className="text-[13px] font-semibold tabular-nums">
          {escaneos.toLocaleString('es-CO')}
          <span className="font-normal text-ink-3">
            {sinTope ? ' · sin límite' : ` de ${tope.toLocaleString('es-CO')}`}
          </span>
        </span>
      </div>

      {sinTope ? null : (
        <>
          <div
            className="h-1.5 w-full overflow-hidden rounded-full bg-raise"
            role="progressbar"
            aria-valuenow={porcentaje}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label="Escaneos consumidos este mes"
          >
            <div
              className={`h-full rounded-full ${apurado ? 'bg-warn' : 'bg-brand'}`}
              style={{ width: `${porcentaje}%` }}
            />
          </div>
          {apurado ? (
            <p className="m-0 text-[12px] text-ink-2">
              Cuando se agoten, tus códigos dejarán de llevar a ningún sitio hasta
              el mes que viene. Los planes de pago no tienen tope.
            </p>
          ) : null}
        </>
      )}
    </div>
  );
}

function EstadoDeLaSuscripcion({
  suscripcion,
  ocupado,
  onPortal,
  onBaja,
}: {
  readonly suscripcion: Suscripcion;
  readonly ocupado: string | null;
  readonly onPortal: () => void;
  readonly onBaja: () => void;
}) {
  const renueva = comoFecha(suscripcion.renuevaEl);
  const termina = comoFecha(suscripcion.terminaEl);

  return (
    <div className="flex flex-col gap-3 rounded-lg border border-line bg-surface px-4 py-4">
      <div className="flex flex-wrap items-center gap-2">
        <strong className="text-[14px]">
          Plan {PLANS[suscripcion.plan].name}
          {suscripcion.ciclo === 'anual' ? ', anual' : ''}
        </strong>
        <span className="rounded-full border border-line px-2 py-0.5 text-[11.5px] text-ink-3">
          {ESTADO_EN_CLARO[suscripcion.estado] ?? suscripcion.estado}
        </span>
        {suscripcion.modoPrueba ? (
          // Sin esto, una suscripcion de la tienda de pruebas es indistinguible
          // de una real y se acaba dando soporte sobre un cobro que no existe.
          <span className="rounded-full border border-line px-2 py-0.5 text-[11.5px] text-ink-3">
            Modo de pruebas
          </span>
        ) : null}
      </div>

      {suscripcion.ultimoCobroFallidoEl ? (
        <Callout tone="warning" title="No hemos podido cobrar tu última cuota">
          Revisa tu tarjeta desde «Gestionar mi suscripción». Tu plan sigue
          funcionando mientras lo intentamos de nuevo: no vamos a apagar tus
          códigos por esto.
        </Callout>
      ) : null}

      <p className="m-0 text-[12.5px] text-ink-2">
        {suscripcion.cancelaAlFinal && termina
          ? `Dada de baja. Tu plan sigue activo hasta el ${termina}, y después tu cuenta volverá al plan Gratis.`
          : renueva
            ? `Se renueva el ${renueva}.`
            : 'Sin próxima fecha de cobro.'}
        {suscripcion.tarjeta?.ultimos4
          ? ` Pagas con ${suscripcion.tarjeta.marca ?? 'tarjeta'} ···· ${suscripcion.tarjeta.ultimos4}.`
          : ''}
      </p>

      {/*
        Los dos con forma de boton, y uno mas callado que el otro.

        "Dar de baja" era `ghost`: texto gris sin borde ni fondo, al lado de
        un boton solido. Eso no se lee como un control, se lee como un pie de
        foto —y para quien mira la pantalla, invisible y ausente son lo
        mismo—. Esconder la salida no retiene a nadie: solo hace que escriba
        pidiendo que le demos de baja, o que reclame el cargo al banco, que
        es bastante peor.

        Sigue siendo el secundario: la accion corriente de quien paga es
        entrar a su suscripcion, no irse.
      */}
      <div className="flex flex-wrap gap-2">
        <Button loading={ocupado === 'portal'} onClick={onPortal}>
          Gestionar mi suscripción
        </Button>
        {!suscripcion.cancelaAlFinal ? (
          <Button variant="secondary" loading={ocupado === 'baja'} onClick={onBaja}>
            Dar de baja
          </Button>
        ) : null}
      </div>
    </div>
  );
}
